import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  AlertCircle,
  Copy,
  Trash2,
  Check,
  ChevronDown,
  MoreVertical,
} from 'lucide-react';
import { TaskDetailOverview } from '@/components/tasks/detail/TaskDetailOverview';
import { TaskDetailComments } from '@/components/tasks/detail/TaskDetailComments';
import { TaskDetailActivity } from '@/components/tasks/detail/TaskDetailActivity';
import { TaskDetailAttachments } from '@/components/tasks/detail/TaskDetailAttachments';
import { TaskDetailChecklist } from '@/components/tasks/detail/TaskDetailChecklist';
import { TaskDetailSubtasks } from '@/components/tasks/detail/TaskDetailSubtasks';
import { TaskDetailLinkedAssets } from '@/components/tasks/detail/TaskDetailLinkedAssets';
import { tasksService } from '@/services/tasks-service';
import type { Task, TaskComment, TaskAttachment, TaskActivity, TaskAssignee } from '@/types/task';
import { useFeatureMode } from '@/hooks/use-feature-mode';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataModeToggle } from '@/components/ui/data-mode-toggle';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { demoAssignees } from '@/data/demo-tasks';
import { useTaskPermissions } from '@/hooks/use-permissions';

// Helper function to get error message based on demo mode - extracted to reduce cognitive complexity (S3776)
function getTaskLoadErrorMessage(isDemo: boolean): string {
  return isDemo 
    ? 'Failed to load demo task. Please refresh the page.' 
    : 'Failed to connect to the API. The Tasks module requires a backend API. Please switch to Demo mode in Feature Settings to view sample data.';
}

// Helper function to filter assignees by search term - extracted to reduce cognitive complexity (S3776)
function filterAssigneesBySearch(assignees: typeof demoAssignees, searchTerm: string) {
  const userAssignees = assignees.filter((a) => a.type === 'user');
  if (!searchTerm) return userAssignees;
  
  const searchLower = searchTerm.toLowerCase();
  return userAssignees.filter((a) => 
    a.name.toLowerCase().includes(searchLower) ||
    a.email.toLowerCase().includes(searchLower)
  );
}

// Helper function to render loading skeleton - extracted to reduce cognitive complexity (S3776)
function TaskDetailLoadingSkeleton() {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b bg-card p-6">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-6 w-96" />
      </div>
      <div className="flex-1 flex">
        <div className="flex-1 p-6">
          <Skeleton className="h-64" />
        </div>
        <div className="w-96 border-l p-6">
          <Skeleton className="h-full" />
        </div>
      </div>
    </div>
  );
}

export default function TaskDetail() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isDemo } = useFeatureMode('workItems');
  
  // Permission checks for task actions
  const { canEdit, canCreate } = useTaskPermissions();
  
  const [task, setTask] = useState<Task | null>(null);
  const [editedTask, setEditedTask] = useState<Task | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [activity, setActivity] = useState<TaskActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [_showBlockedDialog, _setShowBlockedDialog] = useState(false);
  const [_blockedReason, _setBlockedReason] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Duplicate task modal state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateTaskName, setDuplicateTaskName] = useState('');
  const [duplicateAssignee, setDuplicateAssignee] = useState<TaskAssignee | undefined>(undefined);
  const [assigneeSearchOpen, setAssigneeSearchOpen] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [duplicateOptions, setDuplicateOptions] = useState({
    description: true,
    checklists: true,
    subtasks: false,
    linkedAssets: true,
    attachments: false,
    tags: true,
  });

  useEffect(() => {
    loadTaskDetail();
  }, [taskId]);

  const loadTaskDetail = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (!taskId) return;
      
      // Use TasksService which handles both API and demo mode based on feature settings
      const foundTask = await tasksService.getTask(taskId);
      if (foundTask) {
        setTask(foundTask);
        
        // Load related data using tasksService
        const [taskComments, taskAttachments, taskActivity] = await Promise.all([
          tasksService.getTaskComments(taskId),
          tasksService.getTaskAttachments(taskId),
          tasksService.getTaskActivity(taskId),
        ]);
        
        setComments(taskComments);
        setAttachments(taskAttachments);
        setActivity(taskActivity);
      } else {
        setLoadError('Task not found');
      }
    } catch (error) {
      console.error('Failed to load task:', error);
      const errorMessage = getTaskLoadErrorMessage(isDemo);
      
      setLoadError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditedTask(task);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditedTask(null);
    setIsEditMode(false);
  };

  const validateTaskTitle = (title: string): boolean => {
    if (!title.trim()) {
      toast({ title: 'Validation Error', description: 'Task title is required', variant: 'destructive' });
      return false;
    }
    if (title.length > 200) {
      toast({ title: 'Validation Error', description: 'Task title must be 200 characters or less', variant: 'destructive' });
      return false;
    }
    return true;
  };

  const persistChecklistChanges = async (originalTask: Task, edited: Task) => {
    const originalIds = new Set(originalTask.checklists.map(c => c.id));
    const editedIds = new Set(edited.checklists.map(c => c.id));

    for (const checklist of edited.checklists) {
      if (!originalIds.has(checklist.id)) {
        await tasksService.addChecklistItem(edited.id, { title: checklist.title, mandatory: checklist.mandatory });
      } else {
        const original = originalTask.checklists.find(c => c.id === checklist.id);
        if (original && original.completed !== checklist.completed) {
          await tasksService.updateChecklistItem(edited.id, checklist.id, { completed: checklist.completed });
        }
      }
    }
    for (const checklist of originalTask.checklists) {
      if (!editedIds.has(checklist.id)) {
        await tasksService.deleteChecklistItem(edited.id, checklist.id);
      }
    }
  };

  const persistSubtaskChanges = async (originalTask: Task, edited: Task) => {
    const originalIds = new Set((originalTask.subtasks ?? []).map(s => s.id));
    const editedIds = new Set((edited.subtasks ?? []).map(s => s.id));

    for (const subtask of (edited.subtasks ?? [])) {
      if (!originalIds.has(subtask.id)) {
        await tasksService.addSubtask(edited.id, {
          title: subtask.title, assigneeId: subtask.assigneeId, assigneeName: subtask.assigneeName, dueDate: subtask.dueDate,
        });
      } else {
        const original = (originalTask.subtasks ?? []).find(s => s.id === subtask.id);
        if (original && original.status !== subtask.status) {
          await tasksService.updateSubtask(edited.id, subtask.id, { status: subtask.status });
        }
      }
    }
    for (const subtask of (originalTask.subtasks ?? [])) {
      if (!editedIds.has(subtask.id)) {
        await tasksService.deleteSubtask(edited.id, subtask.id);
      }
    }
  };

  const persistLinkedAssetChanges = async (originalTask: Task, edited: Task) => {
    const originalIds = new Set((originalTask.linkedAssets ?? []).map(a => a.id));
    const editedIds = new Set((edited.linkedAssets ?? []).map(a => a.id));

    for (const asset of (edited.linkedAssets ?? [])) {
      if (!originalIds.has(asset.id)) {
        await tasksService.addLink(edited.id, {
          linkType: 'asset', linkedEntityId: asset.assetId, linkedEntityName: asset.assetName, linkedEntityDescription: asset.assetCode ?? undefined,
        });
      }
    }
    for (const asset of (originalTask.linkedAssets ?? [])) {
      if (!editedIds.has(asset.id)) {
        await tasksService.deleteLink(edited.id, asset.id);
      }
    }
  };

  const handleSave = async () => {
    if (!editedTask || !task) return;
    if (!validateTaskTitle(editedTask.title)) return;

    setIsSaving(true);
    try {
      const updateDto: any = {
        title: editedTask.title,
        description: editedTask.description,
        type: editedTask.type,
        priority: editedTask.priority,
        status: editedTask.status,
        assignedToUserId: editedTask.assignee?.id,
        assignedToUserName: editedTask.assignee?.name,
        assignedToUserEmail: editedTask.assignee?.email,
        assignedToUserAvatar: editedTask.assignee?.avatar,
        assignedToUserType: editedTask.assignee?.type,
        dueDate: editedTask.dueDate,
        startDate: editedTask.startDate,
        tags: editedTask.tags,
      };

      const updatedTask = await tasksService.updateTask(editedTask.id, updateDto);
      await persistChecklistChanges(task, editedTask);
      await persistSubtaskChanges(task, editedTask);
      await persistLinkedAssetChanges(task, editedTask);

      setTask(updatedTask);
      setIsEditMode(false);
      setEditedTask(null);
      await loadTaskDetail();
      toast({ title: 'Success', description: 'Task saved successfully' });
    } catch (error) {
      console.error('Failed to save task:', error);
      toast({ title: 'Error', description: 'Failed to save task. Please try again.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = () => {
    if (!task) return;
    setDuplicateTaskName(`${task.title} (Copy)`);
    setDuplicateAssignee(task.assignee);
    setShowDuplicateDialog(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!task) return;
    
    try {
      // Log duplication details for demo purposes
      console.log('Duplicating task with options:', {
        name: duplicateTaskName,
        assignee: duplicateAssignee,
        options: duplicateOptions,
        originalTask: task.id,
      });

      // In demo mode, simulate the creation with a delay
      await new Promise(resolve => setTimeout(resolve, 300));

      toast({
        title: 'Task Duplicated',
        description: `Created new task: ${duplicateTaskName}`,
      });

      setShowDuplicateDialog(false);
      
      // Navigate to tasks list to see the new task
      navigate('/tasks');
    } catch (error) {
      console.error('Failed to duplicate task:', error);
      toast({
        title: 'Error',
        description: 'Failed to duplicate task. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const filteredAssignees = filterAssigneesBySearch(demoAssignees, assigneeSearch);

  if (loading) {
    return <TaskDetailLoadingSkeleton />;
  }

  if (!task) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Work Item Not Found</h2>
          <p className="text-muted-foreground mb-4">
            {loadError ?? "The work item you're looking for doesn't exist or has been deleted."}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={() => navigate('/tasks')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Work Items
            </Button>
            {!isDemo && (
              <DataModeToggle />
            )}
          </div>
        </div>
      </div>
    );
  }

  const commentsCount = comments.length;
  const attachmentsCount = attachments.length;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/tasks')}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight">
                {isEditMode ? editedTask?.title : task.title}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-muted-foreground">
                  {task.assignee ? task.assignee.name : 'Unassigned'}
                </p>
                <span className="text-sm text-muted-foreground">•</span>
                <Badge variant="outline" className="text-xs">
                  {task.status}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditMode ? (
                <>
                  {canEdit && (
                    <Button variant="outline" size="sm" onClick={handleEdit}>
                      Edit
                    </Button>
                  )}
                  {(canCreate || canEdit) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canCreate && (
                          <DropdownMenuItem onClick={handleDuplicate}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate Task
                          </DropdownMenuItem>
                        )}
                        {canEdit && (
                          <DropdownMenuItem className="text-destructive">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Task
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Side - Tabs Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            {/* Tab Bar - Enhanced Enterprise Design matching Team Settings */}
            <div className="border-b bg-[rgb(242,245,245)] dark:bg-muted/30">
              <TabsList className="h-auto p-0 bg-transparent border-0 gap-1">
                <TabsTrigger 
                  value="overview"
                  className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger 
                  value="checklist"
                  className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                >
                  Checklist
                  {task.checklists.length > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-muted/50 text-foreground hover:bg-muted/50">
                      {task.checklists.filter(c => c.completed).length}/{task.checklists.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="subtasks"
                  className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                >
                  Subtasks
                  {(task.subtasks?.length ?? 0) > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-muted/50 text-foreground hover:bg-muted/50">
                      {(task.subtasks ?? []).filter(s => s.status === 'Resolved' || s.status === 'Closed').length}/{task.subtasks?.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="assets"
                  className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                >
                  Assets
                  {(task.linkedAssets?.length ?? 0) > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-muted/50 text-foreground hover:bg-muted/50">
                      {task.linkedAssets?.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="attachments"
                  className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                >
                  Attachments
                  {attachmentsCount > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-muted/50 text-foreground hover:bg-muted/50">
                      {attachmentsCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="comments"
                  className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                >
                  Comments
                  {commentsCount > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-muted/50 text-foreground hover:bg-muted/50">
                      {commentsCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-auto">
              <TabsContent value="overview" className="m-0 p-6">
                <TaskDetailOverview
                  task={isEditMode ? editedTask! : task}
                  onTaskUpdate={isEditMode ? setEditedTask : setTask}
                  isEditMode={isEditMode}
                />
              </TabsContent>

              <TabsContent value="checklist" className="m-0 p-6">
                <TaskDetailChecklist
                  task={isEditMode ? editedTask! : task}
                  onTaskUpdate={isEditMode ? setEditedTask : setTask}
                  isEditMode={isEditMode}
                />
              </TabsContent>

              <TabsContent value="subtasks" className="m-0 p-6">
                <TaskDetailSubtasks
                  task={isEditMode ? editedTask! : task}
                  onTaskUpdate={isEditMode ? setEditedTask : setTask}
                  isEditMode={isEditMode}
                />
              </TabsContent>

              <TabsContent value="assets" className="m-0 p-6">
                <TaskDetailLinkedAssets
                  task={isEditMode ? editedTask! : task}
                  onTaskUpdate={isEditMode ? setEditedTask : setTask}
                  isEditMode={isEditMode}
                />
              </TabsContent>

              <TabsContent value="attachments" className="m-0 p-6">
                <TaskDetailAttachments
                  taskId={task.id}
                  attachments={attachments}
                  onAttachmentAdded={(attachment) => setAttachments([...attachments, attachment])}
                  isEditMode={isEditMode}
                />
              </TabsContent>

              <TabsContent value="comments" className="m-0 p-6">
                <TaskDetailComments
                  taskId={task.id}
                  comments={comments}
                  onCommentAdded={(comment) => setComments(prev => [...prev, comment])}
                  isEditMode={isEditMode}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Right Sidebar - Activity Panel */}
        <div className="w-80 border-l bg-muted/30 overflow-y-auto">
          <div className="p-4 sticky top-0 bg-muted/30 z-10 border-b border-border/40">
            <h3 className="text-sm font-semibold">Activity</h3>
          </div>
          <div className="p-4">
            <TaskDetailActivity
              taskId={task.id}
              activity={activity}
            />
          </div>
        </div>
      </div>

      {/* Duplicate Task Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-background">
          <DialogHeader>
            <DialogTitle>Duplicate Task</DialogTitle>
            <DialogDescription>
              Create a copy of this task with the selected options
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Task Name */}
            <div className="space-y-2">
              <Label htmlFor="task-name">Task Name *</Label>
              <Input
                id="task-name"
                value={duplicateTaskName}
                onChange={(e) => setDuplicateTaskName(e.target.value)}
                placeholder="Enter task name"
              />
            </div>

            {/* Assignee */}
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Popover open={assigneeSearchOpen} onOpenChange={setAssigneeSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={assigneeSearchOpen}
                    className="w-full justify-between"
                  >
                    {duplicateAssignee ? (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={duplicateAssignee.avatar} alt={duplicateAssignee.name} />
                          <AvatarFallback>
                            {duplicateAssignee.name
                              .split(' ')
                              .map((n: string) => n[0])
                              .join('')
                              .toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">{duplicateAssignee.name}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Select user...</span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search users..."
                      value={assigneeSearch}
                      onValueChange={setAssigneeSearch}
                    />
                    <CommandList>
                      <CommandEmpty>No users found.</CommandEmpty>
                      <CommandGroup>
                        {filteredAssignees.map((assignee) => (
                          <CommandItem
                            key={assignee.id}
                            value={assignee.id}
                            onSelect={() => {
                              setDuplicateAssignee(assignee);
                              setAssigneeSearchOpen(false);
                              setAssigneeSearch('');
                            }}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={assignee.avatar} alt={assignee.name} />
                                <AvatarFallback>
                                  {assignee.name
                                    .split(' ')
                                    .map((n) => n[0])
                                    .join('')
                                    .toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex flex-col min-w-0">
                                <span className="font-medium truncate">{assignee.name}</span>
                                <span className="text-xs text-muted-foreground truncate">
                                  {assignee.email}
                                </span>
                              </div>
                            </div>
                            {duplicateAssignee?.id === assignee.id && (
                              <Check className="h-4 w-4 ml-2 flex-shrink-0" />
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* What to Keep */}
            <div className="space-y-3">
              <Label>What to Keep</Label>
              <div className="space-y-3 border rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keep-description"
                    checked={duplicateOptions.description}
                    onCheckedChange={(checked) =>
                      setDuplicateOptions({ ...duplicateOptions, description: checked as boolean })
                    }
                  />
                  <label
                    htmlFor="keep-description"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Description
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keep-checklists"
                    checked={duplicateOptions.checklists}
                    onCheckedChange={(checked) =>
                      setDuplicateOptions({ ...duplicateOptions, checklists: checked as boolean })
                    }
                  />
                  <label
                    htmlFor="keep-checklists"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Checklists ({task?.checklists.length ?? 0})
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keep-subtasks"
                    checked={duplicateOptions.subtasks}
                    onCheckedChange={(checked) =>
                      setDuplicateOptions({ ...duplicateOptions, subtasks: checked as boolean })
                    }
                  />
                  <label
                    htmlFor="keep-subtasks"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Subtasks ({task?.subtasks?.length ?? 0})
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keep-assets"
                    checked={duplicateOptions.linkedAssets}
                    onCheckedChange={(checked) =>
                      setDuplicateOptions({ ...duplicateOptions, linkedAssets: checked as boolean })
                    }
                  />
                  <label
                    htmlFor="keep-assets"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Linked Assets ({task?.linkedAssets?.length ?? 0})
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keep-attachments"
                    checked={duplicateOptions.attachments}
                    onCheckedChange={(checked) =>
                      setDuplicateOptions({ ...duplicateOptions, attachments: checked as boolean })
                    }
                  />
                  <label
                    htmlFor="keep-attachments"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Attachments ({attachments.length + (task?.attachmentsCount ?? 0)})
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="keep-tags"
                    checked={duplicateOptions.tags}
                    onCheckedChange={(checked) =>
                      setDuplicateOptions({ ...duplicateOptions, tags: checked as boolean })
                    }
                  />
                  <label
                    htmlFor="keep-tags"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Tags ({task?.tags.length ?? 0})
                  </label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmDuplicate} disabled={!duplicateTaskName.trim()}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
